/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {OutgoingHttpHeaders} from "http";
import {showDialog} from './eventUtil';
import {warn} from "./miscUtil";
import {FetchMethod, ResponseData} from './interfaces';
import {getJwt} from './jwtUtil';
import {voidFunction} from "./controlUtil";

type ResponseHandler = (response: Response, config: FetchOptions) => void;

export interface FetchOptions {
    onAbort?: VoidFunction;
    baseUrl?: string | null;
    url: string;
    method?: FetchMethod;
    data?: {};
    stringifyData?: boolean;
    headers?: OutgoingHttpHeaders;
    callback?: (data: ResponseData, response: Response) => void;
    errorCallback?: (response: Response | string) => void;
    credentials?: string;
    responseParser?: ResponseHandler;
    errorHandler?: ResponseHandler;
    abortController?: AbortController;
}

let abortController: AbortController;
let defaultHeaders: OutgoingHttpHeaders = {};
let defaultErrorHandler: ResponseHandler | null = null;

function getAbortController(): AbortController | null {
    try {
        if (!('AbortController' in window)) return null;
        return new AbortController();
    } catch {
        return null;
    }
}

const responseDataIsSuccess = (data: ResponseData): boolean => data.status === 'success';

export const setDefaultHeaders = (headers: OutgoingHttpHeaders) => {
    defaultHeaders = headers;
}

export function setDefaultErrorHandler(errorHandler: ResponseHandler | null) {
    defaultErrorHandler = errorHandler;
}

/**
 * Cancel all running requests and refresh the abort controller and signal
 */
export const cancelAllFetch = () => {
    if (abortController) {
        abortController.abort();
        abortController = new AbortController;
    }
};

function bigIntParser(_: string, value: any) {
    if (typeof value === 'bigint')
        return value.toString();
    return value;
}

export function sendFetch(config: FetchOptions): AbortController | null {
    const baseUrl = config.baseUrl || window.settings.platformUrl;
    if (!baseUrl) throw new Error('Base url is not set!');

    config = _prepareConfig(config);
    const {url, method, headers, data} = config;
    const options: { [index: string]: any } = {
        headers,
        method,
        body: config.stringifyData === false ? data : JSON.stringify(data, bigIntParser),
        cache: 'no-store',
        credentials: config.credentials,
        mode: 'cors',
    };
    const abortController = config.abortController ?? getAbortController();
    if (abortController) options.signal = abortController.signal;

    fetch(baseUrl + url, options).then((response: Response) => {
        _handleResponse(response, config);
    }).catch((reason: any) => {
        if (reason.code === DOMException.ABORT_ERR) return config.onAbort?.(); // Request aborted
        else if (config.errorCallback)
            config.errorCallback(reason);
        else if (defaultErrorHandler)
            defaultErrorHandler(reason, config);
    });
    return abortController;
}

function _handleResponse(response: Response, config: FetchOptions) {
    if (config.responseParser)
        return config.responseParser(response, config);

    if (response.ok) {
        if (!config.callback) return;
        if (response.headers.get('content-type') === 'application/json') {
            response.json().then((data: any) => {
                if (data.message === 'Invalid rights') {
                    showDialog('Je hebt niet genoeg rechten voor deze actie.');
                    data.handled = true;
                    if (config.callback) config.callback(data, response);
                } else if (config.callback) {
                    data.success = responseDataIsSuccess(data);
                    config.callback(data, response);
                }
            });
        } else config.callback({status: 'success', success: true}, response);
    } else
        _onErrorResponse(response, config);
}

function _onErrorResponse(response: Response, config: FetchOptions) {
    if (config.errorHandler) config.errorHandler(response, config);
    else if (config.errorCallback) config.errorCallback(response);
    else if (defaultErrorHandler) defaultErrorHandler(response, config);
    else if (config.callback) config.callback({
        status: 'error',
        message: 'Er is iets misgegaan in het systeem.'
    }, response);
}

function _prepareConfig(options: FetchOptions): FetchOptions {
    const fetchOptions: FetchOptions = Object.assign({}, options);
    if (!fetchOptions.method) fetchOptions.method = FetchMethod.GET;
    if (!fetchOptions.callback) fetchOptions.callback = voidFunction;

    if ([FetchMethod.POST, FetchMethod.PUT].includes(fetchOptions.method) && !fetchOptions.data)
        throw new Error('Cannot send POST or PUT without data.');
    else if (fetchOptions.method === FetchMethod.GET && fetchOptions.data)
        warn('Cannot send data with GET requests, data will be ignored.');

    if (fetchOptions.credentials === undefined) fetchOptions.credentials = 'same-origin';
    return setAuthHeader(fetchOptions);
}

function setAuthHeader(options: FetchOptions): FetchOptions {
    const headers: OutgoingHttpHeaders = Object.assign({}, defaultHeaders, options.headers || {});
    const jwt = getJwt();
    if (jwt)
        headers.Authorization = `JWT ${jwt}`;
    options.headers = headers;
    return options;
}